import { Eye, EyeOff, Archive } from "lucide-react";

const fmtDateTime = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

/**
 * Tiny visual indicator showing whether a document has been downloaded by the
 * employee, and whether it has been formally acknowledged (presa visione).
 *
 * 4 states (in order of "compliance":
 *   1. Acknowledged (native eIDAS download) — green, ShieldCheck/Eye
 *   2. Acknowledged legacy (ratified from old portal) — amber, Archive
 *   3. Downloaded but not ack — emerald (only Eye)
 *   4. Never downloaded — gray, EyeOff
 *
 * Props:
 *   - downloadCount: int
 *   - lastDownloadedAt: ISO string (optional)
 *   - acknowledged: bool
 *   - legacyDownload: bool   (true if all downloads are legacy imports)
 *   - showLabel?: bool       (default true; hides the text on mobile / dense rows)
 */
export const DownloadStatusBadge = ({
    downloadCount = 0,
    lastDownloadedAt,
    acknowledged = false,
    legacyDownload = false,
    showLabel = true,
    testId,
}) => {
    let color, Icon, label, title;

    if (downloadCount === 0) {
        color = "border-slate-200 bg-slate-50 text-slate-500";
        Icon = EyeOff;
        label = "Non visto";
        title = "Il dipendente non ha mai scaricato questo documento";
    } else if (legacyDownload) {
        // Ratified legacy download (imported from old portal & marked as ack'd)
        color = "border-amber-300 bg-amber-50 text-amber-700";
        Icon = Archive;
        label = "Visto (legacy)";
        title = `Scaricato dal vecchio portale${
            lastDownloadedAt ? ` il ${fmtDateTime(lastDownloadedAt)}` : ""
        } e ratificato come presa visione.${downloadCount > 1 ? ` ${downloadCount} download totali.` : ""}`;
    } else if (acknowledged) {
        // Native eIDAS download with formal acknowledgment
        color = "border-emerald-300 bg-emerald-50 text-emerald-700";
        Icon = Eye;
        label = "Visto";
        title = `Presa visione confermata${
            lastDownloadedAt ? ` il ${fmtDateTime(lastDownloadedAt)}` : ""
        }.${downloadCount > 1 ? ` ${downloadCount} download totali.` : ""}`;
    } else {
        // Downloaded but no acknowledgment captured
        color = "border-emerald-200 bg-emerald-50 text-emerald-600";
        Icon = Eye;
        label = "Scaricato";
        title = `Scaricato${
            lastDownloadedAt ? ` il ${fmtDateTime(lastDownloadedAt)}` : ""
        }.${downloadCount > 1 ? ` ${downloadCount} download totali.` : ""}`;
    }

    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${color}`}
            title={title}
            data-testid={testId}
        >
            <Icon className="h-3 w-3" />
            {showLabel && <span>{label}</span>}
        </span>
    );
};
